import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  Calendar,
  Clock,
  Loader2,
  RefreshCw,
  Tag,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { LiveChatAISection } from "../components/LiveChatAISection";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
  CartesianGrid
} from "recharts";

const PRIORITY_COLORS = { HIGH: "#ef4444", MEDIUM: "#F5C518" };
const CHART_COLORS = ["#425B76", "#F5C518", "#10b981", "#f59e0b", "#06b6d4", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16"];

const AnalyticsPage = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [volumeView, setVolumeView] = useState("daily");

  const fetchAnalytics = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await axios.get(`${API}/analytics/comprehensive`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      // Error handled silently
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#425B76] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const VOLUME_VIEWS = {
    daily: { data: analytics?.daily_volume, key: "date" },
    weekly: { data: analytics?.weekly_volume, key: "week" },
    monthly: { data: analytics?.monthly_volume, key: "month" },
  };
  const volumeData = VOLUME_VIEWS[volumeView].data;
  const volumeKey = VOLUME_VIEWS[volumeView].key;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
            <p className="text-slate-500 text-sm mt-1">
              Comprehensive ticket analytics and categorization
            </p>
          </div>
          <Button
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing}
            variant="outline"
            className="border-slate-200 gap-2"
            data-testid="refresh-analytics"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#425B76]/10 flex items-center justify-center">
                    <Tag className="w-6 h-6 text-[#425B76]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Most Common Category</p>
                    <p className="text-xl font-bold text-slate-900">
                      {analytics?.summary?.most_common_category || "N/A"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F5C518]/10 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-[#F5C518]" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Avg Daily Tickets</p>
                    <p className="text-xl font-bold text-slate-900">
                      {analytics?.summary?.avg_daily_tickets?.toFixed(1) || "0"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Peak Hour</p>
                    <p className="text-xl font-bold text-slate-900">
                      {analytics?.summary?.peak_hour || "N/A"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Ticket Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#425B76]" />
                Ticket Categories
              </CardTitle>
              <p className="text-sm text-slate-500">AI-categorized ticket breakdown</p>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={analytics?.categories || []}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis 
                      dataKey="category" 
                      type="category" 
                      tick={{ fontSize: 12 }} 
                      width={95}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                      }}
                    />
                    <Bar 
                      dataKey="count" 
                      fill="#425B76" 
                      radius={[0, 4, 4, 0]}
                      name="Tickets"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Volume Charts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-[#425B76]" />
                    Ticket Volume
                  </CardTitle>
                  <p className="text-sm text-slate-500">Track ticket trends over time</p>
                </div>
                <Tabs value={volumeView} onValueChange={setVolumeView}>
                  <TabsList className="bg-slate-100">
                    <TabsTrigger value="daily" className="data-[state=active]:bg-white text-sm">
                      Daily
                    </TabsTrigger>
                    <TabsTrigger value="weekly" className="data-[state=active]:bg-white text-sm">
                      Weekly
                    </TabsTrigger>
                    <TabsTrigger value="monthly" className="data-[state=active]:bg-white text-sm">
                      Monthly
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={volumeData || []} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey={volumeKey} 
                      tick={{ fontSize: 11 }} 
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="tickets" 
                      fill="#F5C518" 
                      radius={[4, 4, 0, 0]}
                      name="Tickets"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Priority & Hourly Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Priority Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="border-slate-200 shadow-sm h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-[#425B76]" />
                  Priority Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analytics?.priorities || []}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        fill="#8884d8"
                        paddingAngle={5}
                        dataKey="count"
                        nameKey="priority"
                        label={({ priority, percent }) => `${priority} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {(analytics?.priorities || []).map((entry, index) => (
                          <Cell 
                            key={`cell-${entry.priority ?? index}`} 
                            fill={
                              PRIORITY_COLORS[entry.priority] || "#10b981"
                            } 
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Hourly Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Card className="border-slate-200 shadow-sm h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#425B76]" />
                  Hourly Distribution
                </CardTitle>
                <p className="text-sm text-slate-500">When tickets are created</p>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics?.hourly_distribution || []}>
                      <defs>
                        <linearGradient id="hourlyGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#425B76" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#425B76" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="hour" 
                        tick={{ fontSize: 10 }}
                        interval={2}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#fff', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="tickets"
                        stroke="#425B76"
                        strokeWidth={2}
                        fill="url(#hourlyGradient)"
                        name="Tickets"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Monthly Trend - Full Width Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#425B76]" />
                Monthly Ticket Trend
              </CardTitle>
              <p className="text-sm text-slate-500">Ticket volume by month</p>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.monthly_volume || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar 
                      dataKey="tickets" 
                      fill="#425B76" 
                      radius={[4, 4, 0, 0]}
                      name="Tickets"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <LiveChatAISection days={30} />
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
